import React from 'react';
import Icon from '../../../components/AppIcon';

const ProductionAnalytics = ({ batches, metrics }) => {
  const calculateEfficiencyMetrics = () => {
    const totalBatches = batches?.length || 0;
    const completedBatches = batches?.filter(b => b?.status === 'completed')?.length || 0;
    const delayedBatches = batches?.filter(b => b?.status === 'delayed')?.length || 0;
    
    const completionRate = totalBatches > 0 ? Math.round((completedBatches / totalBatches) * 100) : 0;
    const delayRate = totalBatches > 0 ? Math.round((delayedBatches / totalBatches) * 100) : 0;
    
    return { completionRate, delayRate };
  };

  const { completionRate, delayRate } = calculateEfficiencyMetrics();

  const qualityMetrics = [
    { label: 'Taux de réussite', value: '94%', icon: 'CheckCircle', color: 'text-success' },
    { label: 'Défauts détectés', value: '12', icon: 'AlertTriangle', color: 'text-warning' },
    { label: 'Retouches nécessaires', value: '8', icon: 'RefreshCw', color: 'text-primary' }
  ];

  const deliveryPerformance = [
    { label: 'Livraisons à temps', value: `${metrics?.onTimeRate || 0}%`, icon: 'Target', color: 'text-success' },
    { label: 'Retards', value: `${delayRate}%`, icon: 'Clock', color: 'text-error' },
    { label: 'Taux de complétion', value: `${completionRate}%`, icon: 'TrendingUp', color: 'text-primary' }
  ];

  return (
    <div className="space-y-6">
      {/* Efficiency Overview */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Efficacité de Production</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="text-sm text-gray-600 mb-2">Taux de complétion</div>
            <div className="flex items-end gap-2 mb-2">
              <div className="text-3xl font-bold text-gray-900">{completionRate}%</div>
              <div className="text-sm text-success mb-1">+5%</div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-success rounded-full h-2"
                style={{ width: `${completionRate}%` }}
              />
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-2">Progression moyenne</div>
            <div className="flex items-end gap-2 mb-2">
              <div className="text-3xl font-bold text-gray-900">{metrics?.avgProgress || 0}%</div>
              <div className="text-sm text-primary mb-1">+2%</div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary rounded-full h-2"
                style={{ width: `${metrics?.avgProgress || 0}%` }}
              />
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-2">Taux de retard</div>
            <div className="flex items-end gap-2 mb-2">
              <div className="text-3xl font-bold text-gray-900">{delayRate}%</div>
              <div className="text-sm text-success mb-1">-3%</div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-error rounded-full h-2"
                style={{ width: `${delayRate}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Quality Metrics */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Métriques Qualité</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {qualityMetrics?.map((metric, index) => (
            <div key={index} className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <Icon name={metric?.icon} size={24} className={metric?.color} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{metric?.value}</div>
                <div className="text-sm text-gray-600">{metric?.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delivery Performance */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Performance Livraison</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {deliveryPerformance?.map((metric, index) => (
            <div key={index} className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <Icon name={metric?.icon} size={24} className={metric?.color} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{metric?.value}</div>
                <div className="text-sm text-gray-600">{metric?.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* QR Tracking Integration */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Intégration Traçabilité QR</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-primary/5 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="QrCode" size={20} className="text-primary" />
              <span className="font-semibold text-gray-900">Scans totaux</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{batches?.length * 2}</div>
            <div className="text-sm text-gray-600">Moyenne: 2 scans/lot</div>
          </div>
          <div className="bg-success/5 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="CheckCircle" size={20} className="text-success" />
              <span className="font-semibold text-gray-900">Traçabilité complète</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">98%</div>
            <div className="text-sm text-gray-600">Lots avec historique complet</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionAnalytics;